import type { Block, HeadingBlock } from './astTypes'
import { getBlockText } from './astUtils'
import { parseSectionOpening, stripWritingNotes } from './writingSyntax'

/**
 * Headings whose title starts with this prefix are marked for the book TOC sidebar
 * (aggregated list); the prefix is stripped for display. The raw title (including ^)
 * remains in the document for navigation and stable outline ids.
 */
export const TOC_CHAPTER_SECTION_HEADING_PREFIX = '^' as const

export function isChapterSectionHeadingText(raw: string): boolean {
  return parseSectionOpening(raw) !== null
}

/** Strips a single leading ^ and following whitespace for sidebar / reader labels. */
export function stripChapterSectionHeadingPrefix(raw: string): string {
  const clean = stripWritingNotes([{ type: 'paragraph', id: 'label', content: [{ type: 'text', text: raw }] }])
    .map(getBlockText)
    .join('')
  return parseSectionOpening(clean)?.title ?? clean.trimStart()
}

/**
 * After `^` is stripped, headings may still use bracket segments like `[topic][Display Title]`.
 * For sidebar/TOC we prefer the last bracket's inner text when there are at least two segments.
 */
export function formatChapterSectionHeadingForSidebarDisplay(afterCaret: string): string {
  const s = afterCaret.trim()
  if (!s) {
    return s
  }
  const segments = s.match(/\[[^\]]*\]/g)
  if (segments && segments.length >= 2) {
    const last = segments[segments.length - 1].slice(1, -1).trim()
    if (last.length > 0) {
      return last
    }
  }
  return s
}

/**
 * Reader AST: remove `^`-marked heading blocks from reader content. The reader page
 * renders a React-level ornament between consecutive sections in JSX.
 */
export function stripChapterSectionHeadingPrefixFromReaderBlocks(blocks: Block[]): Block[] {
  return blocks.filter((block) => {
    if (block.type !== 'heading') {
      return true
    }
    const raw = getBlockText(block as HeadingBlock)
    return !isChapterSectionHeadingText(raw)
  })
}
